import { z, uuidSchema } from '@kc3/validation';
import { baseEventFields } from './_base.js';
const ChannelEnum = z.enum(['email', 'sms', 'whatsapp', 'facebook', 'instagram', 'chat', 'voice', 'web', 'youtube', 'x', 'telegram']);
export const ChannelMessageInboundEventSchema = z.object({
    type: z.literal('channel.message.inbound'),
    ...baseEventFields,
    channel: ChannelEnum,
    senderHandle: z.string().min(1),
    senderName: z.string().optional(),
    externalId: z.string().min(1),
    rawPayload: z.record(z.unknown()),
});
export const ConversationOpenedEventSchema = z.object({
    type: z.literal('conversation.opened'),
    ...baseEventFields,
    conversationId: uuidSchema,
    referenceId: z.string().min(1),
    channelOrigin: ChannelEnum,
    assignedAgentId: uuidSchema.nullable(),
    assignedQueueId: uuidSchema.nullable(),
    primaryHandle: z.string().min(1),
    displayName: z.string().min(1),
    lastMessageAt: z.string().datetime(),
    slaState: z.enum(['within', 'approaching', 'breached', 'met', 'none']),
});
export const ConversationMessageNewEventSchema = z.object({
    type: z.literal('conversation.message.new'),
    ...baseEventFields,
    conversationId: uuidSchema,
    messageId: uuidSchema,
    direction: z.enum(['inbound', 'outbound']),
    channel: ChannelEnum,
    body: z.string().optional(),
    attachments: z
        .array(z.object({
        id: uuidSchema,
        filename: z.string(),
        mimeType: z.string(),
        sizeBytes: z.number().int().nonnegative(),
    }))
        .optional(),
    lastMessagePreview: z.string().min(1),
    lastMessageAt: z.string().datetime(),
    // The client (clients.id) this conversation belongs to, when known. Populated
    // on agent-authored (outbound) replies so the ws-gateway can fan the event to
    // a per-client room the client-portal ticket list subscribes to for live
    // unread updates. Nullable/optional: inbound customer messages and anonymous
    // web-chat paths have no known client.
    clientId: uuidSchema.nullable().optional(),
    // AG-05: true on AI-authored messages when the tenant's AI disclosure is on,
    // so the website widget can label them. Omitted otherwise.
    aiGenerated: z.boolean().optional(),
});
export const ConversationMessageOutboundEventSchema = z.object({
    type: z.literal('conversation.message.outbound'),
    ...baseEventFields,
    conversationId: uuidSchema,
    messageId: uuidSchema,
    channel: ChannelEnum,
    // Primary recipient(s). Email replies may address several people at once,
    // in which case this carries the comma-separated To list.
    recipientHandle: z.string().min(1),
    // Email-only Cc / Bcc lists (additive/optional — other channels omit them).
    cc: z.array(z.string().min(1)).optional(),
    bcc: z.array(z.string().min(1)).optional(),
    contentText: z.string(),
    contentHtml: z.string().nullable().optional(),
    inReplyTo: z.string().nullable().optional(),
    subject: z.string().nullable().optional(),
    priorAttemptCount: z.number().int().nonnegative().optional(),
    // Optional provider override — when set, the outbound consumer uses this
    // instead of looking up channel_settings (e.g. 'facebook_comment' for
    // replying to Facebook Page post comments vs 'messenger' for DMs).
    provider: z.string().optional(),
    // UI-1d: structured WhatsApp template kept for the adapter (not pre-rendered).
    template: z
        .object({
        name: z.string().min(1),
        languageCode: z.string().min(1),
        variables: z.array(z.string()),
    })
        .optional(),
    // UI-1c/1d: linked attachment ids (additive/optional).
    attachments: z.array(uuidSchema).optional(),
    // Display name for the sender (e.g. AI agent name for auto-replies).
    agentDisplayName: z.string().optional(),
    // The single STOP acknowledgement. The consumer only honours it when the
    // message row itself was written with the same purpose (see channel-outbound).
    purpose: z.literal('stop_confirmation').optional(),
    // EM-13: 'commercial' email gets suppression + a sender-identification footer when the tenant has
    // email_outbound_guards on. Absent = 'transactional' (unchanged behaviour).
    messageClass: z.enum(['transactional', 'commercial']).optional(),
    // EM-22: true for machine-authored replies (AI auto-reply). Email gets Auto-Submitted headers.
    automated: z.boolean().optional(),
    // EM-10: id of the human agent (users.id) who composed the message. Lets the email first-contact
    // limit apply per agent as well as per tenant. Absent (AI/system/older producers) = tenant limit only.
    agentId: uuidSchema.optional(),
});
export const ConversationSlaBreachedEventSchema = z.object({
    type: z.literal('conversation.sla_breached'),
    ...baseEventFields,
    conversationId: uuidSchema,
    policyType: z.enum(['sla', 'ola']),
    policyId: uuidSchema,
    breachedAt: z.string().datetime(),
});
export const ConversationTransferredEventSchema = z.object({
    type: z.literal('conversation.transferred'),
    ...baseEventFields,
    conversationId: uuidSchema,
    transferType: z.enum(['warm', 'cold', 'queue', 'automatic']),
    fromAgentId: uuidSchema.nullable(),
    toAgentId: uuidSchema.nullable(),
    fromQueueId: uuidSchema.nullable(),
    toQueueId: uuidSchema.nullable(),
});
export const ConversationNotificationEventSchema = z.object({
    type: z.literal('conversation.notification'),
    ...baseEventFields,
    conversationId: uuidSchema.nullable(),
    eventKey: z.string().min(1),
    audience: z.enum(['agent', 'customer']),
    recipientId: uuidSchema.nullable(),
    address: z.string().nullable(),
    channel: z.enum(['email', 'sms', 'in_app', 'push']),
    subject: z.string().nullable(),
    body: z.string(),
    outboundEmailAccountId: z.string().nullable(),
});
export const CampaignNotifTriggerEventSchema = z.object({
    type: z.literal('campaign.notif.trigger'),
    ...baseEventFields,
    conversationId: uuidSchema,
    campaignId: uuidSchema,
    eventType: z.string().min(1),
    typeId: uuidSchema.nullable().optional(),
    stateId: uuidSchema.nullable().optional(),
    categoryId: uuidSchema.nullable().optional(),
    subcategoryId: uuidSchema.nullable().optional(),
    impactId: uuidSchema.nullable().optional(),
    priorityId: uuidSchema.nullable().optional(),
    complexityId: uuidSchema.nullable().optional(),
    urgencyId: uuidSchema.nullable().optional(),
    assignedAgentId: uuidSchema.nullable().optional(),
    assignedTeamId: uuidSchema.nullable().optional(),
    clientId: uuidSchema.nullable().optional(),
    context: z.record(z.unknown()),
    correlationId: z.string().min(1),
});
export const CampaignRuleChangedEventSchema = z.object({
    type: z.literal('campaign.rule.changed'),
    ...baseEventFields,
    campaignId: uuidSchema,
    action: z.enum(['upsert', 'delete']),
});
export const ConversationEmbeddingRequestedEventSchema = z.object({
    type: z.literal('conversation.embedding.requested'),
    ...baseEventFields,
    conversationId: uuidSchema,
    messageId: uuidSchema,
});
// UI-1e: message-level approval subsystem socket events.
export const ConversationMessageApprovalRequestedEventSchema = z.object({
    type: z.literal('conversation.message_approval.requested'),
    ...baseEventFields,
    conversationId: uuidSchema,
    messageId: uuidSchema,
    approvalId: uuidSchema,
    // agents.id (NOT users.id / jwtPayload.sub) — resolve via "SELECT id FROM agents WHERE user_id = <jwt sub>" before publishing.
    requestedByAgentId: uuidSchema,
    approverAgentId: uuidSchema.nullable(),
    contentPreview: z.string().optional(),
});
export const ConversationMessageApprovalDecidedEventSchema = z.object({
    type: z.literal('conversation.message_approval.decided'),
    ...baseEventFields,
    conversationId: uuidSchema,
    messageId: uuidSchema,
    approvalId: uuidSchema,
    decision: z.enum(['approved', 'rejected', 'cancelled']),
    // agents.id (NOT users.id / jwtPayload.sub) — see requestedByAgentId above.
    decidedByAgentId: uuidSchema.nullable(),
    rejectionReason: z.string().optional(),
});
// UI-1f: co-agent membership socket events.
export const ConversationCoAgentAddedEventSchema = z.object({
    type: z.literal('conversation.co_agent.added'),
    ...baseEventFields,
    conversationId: uuidSchema,
    agentId: uuidSchema,
    addedByAgentId: uuidSchema.nullable(),
});
export const ConversationCoAgentRemovedEventSchema = z.object({
    type: z.literal('conversation.co_agent.removed'),
    ...baseEventFields,
    conversationId: uuidSchema,
    agentId: uuidSchema,
    removedByAgentId: uuidSchema.nullable(),
});
export const ConversationClosedEventSchema = z.object({
    type: z.literal('conversation.closed'),
    ...baseEventFields,
    conversationId: uuidSchema,
    closedAt: z.string().datetime(),
    resolutionType: z.enum(['resolved', 'merged', 'cancelled']),
});
//# sourceMappingURL=conversation.js.map